export const PHOTO_UPLOAD_CONCURRENCY = 3;

let jobSeq = 0;

export function createPhotoJobs(reportId, files) {
  return Array.from(files || []).map((file, index) => ({
    id: `photo-job-${++jobSeq}`,
    reportId: String(reportId),
    file,
    name: file?.name || `photo-${index + 1}`,
    status: "waiting",
    error: null,
  }));
}

export function summarizePhotoJobs(jobs = []) {
  const summary = {
    total: jobs.length,
    waiting: 0,
    uploading: 0,
    ready: 0,
    failed: 0,
  };
  for (const job of jobs) {
    if (job.status === "waiting") summary.waiting += 1;
    else if (job.status === "uploading") summary.uploading += 1;
    else if (job.status === "ready") summary.ready += 1;
    else if (job.status === "failed") summary.failed += 1;
  }
  summary.active = summary.waiting + summary.uploading;
  summary.current = Math.min(summary.ready + summary.uploading, summary.total);
  return summary;
}

export function claimNextWaitingJob(jobs) {
  const index = jobs.findIndex((job) => job.status === "waiting");
  if (index === -1) return { jobs, claimed: null };
  const claimed = { ...jobs[index], status: "uploading", error: null };
  const next = jobs.slice();
  next[index] = claimed;
  return { jobs: next, claimed };
}

export function patchJob(jobs, id, patch) {
  return jobs.map((job) => (job.id === id ? { ...job, ...patch } : job));
}

export function retryJob(jobs, id) {
  return jobs.map((job) =>
    job.id === id && job.status === "failed"
      ? { ...job, status: "waiting", error: null }
      : job
  );
}

export function retryAllFailedJobs(jobs) {
  return jobs.map((job) =>
    job.status === "failed" ? { ...job, status: "waiting", error: null } : job
  );
}

/**
 * Worker pool over waiting jobs. getJobs/setJobs must be synchronous
 * (e.g. a ref) so two workers cannot claim the same photo.
 */
export async function runPhotoUploadWorkers({
  getJobs,
  setJobs,
  concurrency = PHOTO_UPLOAD_CONCURRENCY,
  uploadOne,
  captureException,
}) {
  const workerCount = Math.max(1, concurrency);
  const worker = async () => {
    while (true) {
      const { jobs, claimed } = claimNextWaitingJob(getJobs());
      if (!claimed) return;
      setJobs(jobs);
      try {
        await uploadOne(claimed.reportId, claimed.file);
        setJobs(patchJob(getJobs(), claimed.id, { status: "ready", error: null }));
      } catch (error) {
        captureException?.(error, claimed);
        setJobs(
          patchJob(getJobs(), claimed.id, {
            status: "failed",
            error: error?.message || "Upload failed",
          })
        );
      }
    }
  };

  await Promise.all(Array.from({ length: workerCount }, () => worker()));
}
